import React, { createContext, useContext, useMemo, useState } from 'react';

interface BankInformationFooterContextValue {
  showAddNewAccount: boolean;
  setShowAddNewAccount: (showAddNewAccount: boolean) => void;
  isBankSearchShown: boolean;
  setIsBankSearchShown: (isBankSearchShown: boolean) => void;
  hasNoSearchResults: boolean;
  setHasNoSearchResults: (hasNoSearchResults: boolean) => void;
  startAuthorizationContainer: HTMLElement | null;
  setStartAuthorizationContainer: (container: HTMLElement | null) => void;
}

// The bank step, its header and its footer are rendered separately by the Stepper, so they
// share the bank search state, and the footer's place for the verify button, here.
const BankInformationFooterContext = createContext<BankInformationFooterContextValue>(
  {
    showAddNewAccount: false,
    setShowAddNewAccount: () => undefined,
    isBankSearchShown: false,
    setIsBankSearchShown: () => undefined,
    hasNoSearchResults: false,
    setHasNoSearchResults: () => undefined,
    startAuthorizationContainer: null,
    setStartAuthorizationContainer: () => undefined,
  }
);

export const BankInformationFooterProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [showAddNewAccount, setShowAddNewAccount] = useState(false);
  const [isBankSearchShown, setIsBankSearchShown] = useState(false);
  const [hasNoSearchResults, setHasNoSearchResults] = useState(false);
  const [
    startAuthorizationContainer,
    setStartAuthorizationContainer,
  ] = useState<HTMLElement | null>(null);
  const value = useMemo(
    () => ({
      showAddNewAccount,
      setShowAddNewAccount,
      isBankSearchShown,
      setIsBankSearchShown,
      hasNoSearchResults,
      setHasNoSearchResults,
      startAuthorizationContainer,
      setStartAuthorizationContainer,
    }),
    [
      showAddNewAccount,
      isBankSearchShown,
      hasNoSearchResults,
      startAuthorizationContainer,
    ]
  );

  return (
    <BankInformationFooterContext.Provider value={value}>
      {children}
    </BankInformationFooterContext.Provider>
  );
};

export const useBankInformationFooter = () =>
  useContext(BankInformationFooterContext);
